import type { Apollo } from 'src/gql/generated';

type ExternalAccess =
  Apollo.GetWorkflowFileExternalAccessQuery['workflowFileExternalAccess'][number];

const MAX_NAMED_ACCOUNTS = 3;

export const SHARE_WITH_EVERYONE_MESSAGE =
  'Everyone on this workflow, including people outside your org, will be able to open this document.';

/**
 * The confirmation shown before a file becomes Internal. When outsiders can open it, names the
 * first three accounts and counts the rest, e.g. "Ana, Ben, Acme and 2 others can open this
 * document today."
 */
export function getMakeInternalMessage(access: readonly ExternalAccess[]): string {
  if (!access.length) return 'Only people in your org will see this document in the workflow.';

  const names = access.map(({ account }) => getAccountName(account));
  return `${joinNames(names)} can open this document today. Making it internal removes their access.`;
}

function joinNames(names: readonly string[]): string {
  if (names.length <= 1) return names.join('');
  if (names.length <= MAX_NAMED_ACCOUNTS)
    return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

  const otherCount = names.length - MAX_NAMED_ACCOUNTS;
  const others = otherCount === 1 ? '1 other' : `${otherCount} others`;
  return `${names.slice(0, MAX_NAMED_ACCOUNTS).join(', ')} and ${others}`;
}

function getAccountName(account: ExternalAccess['account']): string {
  switch (account.__typename) {
    case 'Org':
      return account.name;
    case 'User':
      return account.displayName || account.email;
  }
}
